import "./index.css";
import React from "react";
import { Composition } from "remotion";
import { SocialReel, SquarePost, PowerPicksReel } from "./Composition";
import { ProductVideo } from "./compositions/ProductVideo";
import { ExplainerVideo } from "./compositions/ExplainerVideo";
import { StatsVideo } from "./compositions/StatsVideo";
import { SportsStatsBug } from "./compositions/SportsStatsBug";
import { CharacterSlide } from "./compositions/CharacterSlide";
import { AnimatedChart } from "./compositions/AnimatedChart";

// Derive a scene-based composition's total duration from its scenes prop so the
// rendered video always spans EXACTLY the scenes present (their copy included) —
// never the hardcoded default, which cut off extra scenes / left blank tails.
const sceneDuration = ({ props }: { props: Record<string, unknown> }) => {
  const s = (props.scenes as Array<{ duration?: number }>) || [];
  const total = s.reduce((a, x) => a + (Number(x.duration) || 0), 0);
  return { durationInFrames: Math.max(1, Math.round(total)) };
};

export const RemotionRoot: React.FC = () => {
  return (
    <>
      {/* ─────────────────────────────────────────────────────────────────────
          SOCIAL REELS — SocialReel / PowerPicksReel
      ───────────────────────────────────────────────────────────────────── */}

      <Composition id="SocialReel" component={SocialReel as unknown as React.ComponentType<Record<string, unknown>>}
        durationInFrames={270} fps={30} width={1080} height={1920}
        calculateMetadata={sceneDuration}
        defaultProps={{ brandName: "ZERO SOUL AI", accentColor: "#38bdf8",
          scenes: [
            { id: "s1", title: "Headline", body: "Supporting text here", duration: 90 },
            { id: "s2", title: "Point Two", body: "More details", duration: 90 },
            { id: "s3", title: "Call to Action", body: "Follow for more", duration: 90 },
          ],
        }}
      />

      <Composition id="PowerPicksReel" component={PowerPicksReel as unknown as React.ComponentType<Record<string, unknown>>}
        durationInFrames={270} fps={30} width={1080} height={1920}
        calculateMetadata={sceneDuration}
        defaultProps={{ brandName: "POWERPICKS", accentColor: "#38bdf8",
          scenes: [
            { id: "hook", title: "73% of bettors lose.", body: "Not because of bad luck.", duration: 75,
              stat: "73%", statLabel: "average loss rate",
              bg: "linear-gradient(160deg, #020617 0%, #0a1628 60%, #020617 100%)",
            },
            { id: "value", title: "The edge is real.", body: "Sharp money alerts. Live line movements. Proven win rates.",
              duration: 100, bg: "linear-gradient(160deg, #020617 0%, #091a2e 60%, #020617 100%)",
            },
            { id: "cta", title: "Join PowerPicks AI.", body: "7 tools. Real-time intelligence. $33/week.",
              duration: 95, stat: "$33", statLabel: "per week",
              bg: "linear-gradient(160deg, #020617 0%, #0f172a 60%, #020617 100%)",
            },
          ],
        }}
      />

      {/* ─────────────────────────────────────────────────────────────────────
          SQUARE / LANDSCAPE / PORTRAIT POSTS
      ───────────────────────────────────────────────────────────────────── */}

      <Composition id="Social45" component={SocialReel as unknown as React.ComponentType<Record<string, unknown>>}
        durationInFrames={270} fps={30} width={1080} height={1350}
        calculateMetadata={sceneDuration}
        defaultProps={{ brandName: "ZERO SOUL AI", accentColor: "#38bdf8",
          scenes: [
            { id: "s1", title: "Headline", body: "Supporting text here", duration: 90 },
            { id: "s2", title: "Point Two", body: "More details", duration: 90 },
            { id: "s3", title: "Call to Action", body: "Follow for more", duration: 90 },
          ],
        }}
      />

      <Composition id="PowerPicks45" component={PowerPicksReel as unknown as React.ComponentType<Record<string, unknown>>}
        durationInFrames={270} fps={30} width={1080} height={1350}
        calculateMetadata={sceneDuration}
        defaultProps={{ brandName: "POWERPICKS", accentColor: "#38bdf8",
          scenes: [
            { id: "hook", title: "73% of bettors lose.", body: "Not because of bad luck.",
              duration: 75, stat: "73%", statLabel: "average loss rate",
              bg: "linear-gradient(160deg, #020617 0%, #0a1628 60%, #020617 100%)",
            },
            { id: "value", title: "The edge is real.", body: "Sharp money alerts. Live line movements. Proven win rates.",
              duration: 100, bg: "linear-gradient(160deg, #020617 0%, #091a2e 60%, #020617 100%)",
            },
            { id: "cta", title: "Join PowerPicks AI.", body: "7 tools. Real-time intelligence. $33/week.",
              duration: 95, stat: "$33", statLabel: "per week",
              bg: "linear-gradient(160deg, #020617 0%, #0f172a 60%, #020617 100%)",
            },
          ],
        }}
      />

      <Composition id="SquarePost" component={SquarePost as unknown as React.ComponentType<Record<string, unknown>>}
        durationInFrames={150} fps={30} width={1080} height={1080}
        defaultProps={{ title: "Post Title", subtitle: "Supporting detail", tag: "#trending", brandName: "ZERO SOUL AI", accentColor: "#38bdf8" }}
      />

      <Composition id="LandscapePost" component={SquarePost as unknown as React.ComponentType<Record<string, unknown>>}
        durationInFrames={180} fps={30} width={1920} height={1080}
        defaultProps={{ title: "YouTube / LinkedIn", subtitle: "Landscape format content", tag: "#content", brandName: "ZERO SOUL AI", accentColor: "#38bdf8" }}
      />

      <Composition id="PowerPicksSquare" component={SquarePost as unknown as React.ComponentType<Record<string, unknown>>}
        durationInFrames={150} fps={30} width={1080} height={1080}
        defaultProps={{ title: "The Smart Edge", subtitle: "Real-time sports betting intelligence", tag: "#PowerPicksAI", brandName: "POWERPICKS", accentColor: "#38bdf8", stat: "73%" }}
      />

      {/* ─────────────────────────────────────────────────────────────────────
          STORY / EXPLAINER / PRODUCT
      ───────────────────────────────────────────────────────────────────── */}

      <Composition id="PowerPicksExplainer22" component={PowerPicksReel as unknown as React.ComponentType<Record<string, unknown>>}
        durationInFrames={330} fps={30} width={1080} height={1920}
        calculateMetadata={sceneDuration}
        defaultProps={{ brandName: "POWERPICKS", accentColor: "#38bdf8",
          scenes: [
            { id: "hook", title: "Not a tout. Not a tipster.", body: "Something different.",
              duration: 80, stat: "#22", statLabel: "what is powerpicks?",
              bg: "linear-gradient(160deg, #020617 0%, #0a0f1e 60%, #020617 100%)",
            },
            { id: "what", title: "PowerPicks AI is a full betting intelligence system.",
              body: "Sharp money alerts. Line movements. Injury signals. Public vs sharp splits.",
              duration: 90, imageUrl: "images/freepik_346a9c5d.jpg", overlayOpacity: 0.65,
              bg: "linear-gradient(160deg, #020617 0%, #091a2e 60%, #020617 100%)",
            },
            { id: "tools", title: "7 tools. Real-time intelligence.", body: "NFL · NBA · MLB · NHL. Live, all season.",
              duration: 80, stat: "7", statLabel: "AI-powered tools",
              bg: "linear-gradient(160deg, #020617 0%, #0c1a12 60%, #020617 100%)",
            },
            { id: "cta", title: "Join PowerPicks AI.", body: "The edge the books don't want you to have.",
              duration: 80, stat: "$33", statLabel: "per week",
              bg: "linear-gradient(160deg, #020617 0%, #0f172a 60%, #020617 100%)",
            },
          ],
        }}
      />

      <Composition id="ProductVideo" component={ProductVideo as unknown as React.ComponentType<Record<string, unknown>>}
        durationInFrames={345} fps={30} width={1080} height={1920}
        defaultProps={{
          productName: "Your Product", tagline: "The tagline that stops the scroll.",
          features: [
            { icon: "⚡", title: "Feature One", desc: "Brief description of benefit" },
            { icon: "🎯", title: "Feature Two", desc: "Brief description of benefit" },
            { icon: "🔥", title: "Feature Three", desc: "Brief description of benefit" },
          ],
          price: "$33", priceLabel: "per week", ctaText: "Start today.", accentColor: "#38bdf8",
        }}
      />

      <Composition id="ExplainerVideo" component={ExplainerVideo as unknown as React.ComponentType<Record<string, unknown>>}
        durationInFrames={505} fps={30} width={1080} height={1920}
        defaultProps={{
          topic: "Your Topic", problemStatement: "Most people do X wrong.",
          solutionName: "The Solution", solutionDesc: "A clear description of what it does and who it helps.",
          steps: [
            { num: "1", title: "Step One", desc: "What happens in this step" },
            { num: "2", title: "Step Two", desc: "What happens in this step" },
            { num: "3", title: "Step Three", desc: "What happens in this step" },
          ],
          result: "The outcome you've been waiting for.", resultStat: "3x",
          ctaText: "Try it today.", ctaSubtext: "No experience needed.", accentColor: "#38bdf8",
        }}
      />

      {/* ─────────────────────────────────────────────────────────────────────
          STATS / PICKS
      ───────────────────────────────────────────────────────────────────── */}

      <Composition id="StatsVideo" component={StatsVideo as unknown as React.ComponentType<Record<string, unknown>>}
        durationInFrames={450} fps={30} width={1080} height={1920}
        defaultProps={{
          brandName: "POWERPICKS", accentColor: "#38bdf8",
          winRate: 77, winRateLabel: "win rate this season", recordStat: "23-7",
          headline: "Sharp money doesn't lie.",
          picks: [
            { game: "Lakers vs Warriors", pick: "Lakers -3.5", odds: "-110", confidence: 82, sport: "NBA" },
            { game: "Chiefs vs Bills", pick: "Over 48.5", odds: "-115", confidence: 74, sport: "NFL" },
            { game: "Yankees vs Red Sox", pick: "Yankees ML", odds: "-130", confidence: 68, sport: "MLB" },
          ],
        }}
      />

      {/* ─────────────────────────────────────────────────────────────────────
          BROADCAST OVERLAYS (chroma-key magenta bg)
      ───────────────────────────────────────────────────────────────────── */}

      <Composition id="SportsStatsBug" component={SportsStatsBug as unknown as React.ComponentType<Record<string, unknown>>}
        durationInFrames={300} fps={30} width={1080} height={1920}
        calculateMetadata={({ props }: { props: Record<string, unknown> }) => ({
          durationInFrames: (props["totalFrames"] as number) ?? 300,
        })}
        defaultProps={{
          type: "lower_third", playerName: "LEBRON JAMES", teamName: "LOS ANGELES LAKERS",
          jersey: "23", statLine: "34 PTS  •  12 AST  •  8 REB",
          accentColor: "#38bdf8", brandName: "POWERPICKS", totalFrames: 150,
        }}
      />

      {/* ─────────────────────────────────────────────────────────────────────
          CHARACTER SLIDES — c1 / c2 / c3 (kinetic text, Ken Burns)
      ───────────────────────────────────────────────────────────────────── */}

      <Composition id="CharacterSlide-c1" component={CharacterSlide as unknown as React.ComponentType<Record<string, unknown>>}
        durationInFrames={180} fps={30} width={1080} height={1920}
        defaultProps={{
          characterId: "c1",
          quote: "I used to guess. Now I just follow the sharp money.",
          location: "Dorm Room", sport: "NBA",
          accentColor: "#38bdf8", brandName: "POWERPICKS",
        }}
      />

      <Composition id="CharacterSlide-c2" component={CharacterSlide as unknown as React.ComponentType<Record<string, unknown>>}
        durationInFrames={180} fps={30} width={1080} height={1920}
        defaultProps={{
          characterId: "c2",
          quote: "Real-time data finds what gut feelings miss.",
          location: "Home Office", sport: "NFL",
          accentColor: "#38bdf8", brandName: "POWERPICKS",
        }}
      />

      <Composition id="CharacterSlide-c3" component={CharacterSlide as unknown as React.ComponentType<Record<string, unknown>>}
        durationInFrames={180} fps={30} width={1080} height={1920}
        defaultProps={{
          characterId: "c3",
          quote: "The edge was always there. I just needed the right tool.",
          location: "Lounge", sport: "MLB",
          accentColor: "#38bdf8", brandName: "POWERPICKS",
        }}
      />

      {/* ─────────────────────────────────────────────────────────────────────
          ANIMATED CHART — season performance bar chart
      ───────────────────────────────────────────────────────────────────── */}

      <Composition id="AnimatedChart" component={AnimatedChart as unknown as React.ComponentType<Record<string, unknown>>}
        durationInFrames={300} fps={30} width={1080} height={1920}
        defaultProps={{
          title: "Season Performance",
          subtitle: "Win rate by week — current season",
          overallWinRate: 77, overallRecord: "23-7",
          accentColor: "#38bdf8", brandName: "POWERPICKS",
          weeks: [
            { label: "Wk 1", wins: 4, losses: 2, winRate: 67 },
            { label: "Wk 2", wins: 5, losses: 1, winRate: 83 },
            { label: "Wk 3", wins: 3, losses: 2, winRate: 60 },
            { label: "Wk 4", wins: 5, losses: 0, winRate: 100 },
            { label: "Wk 5", wins: 3, losses: 2, winRate: 60 },
            { label: "Wk 6", wins: 3, losses: 1, winRate: 75 },
          ],
        }}
      />

      <Composition id="AnimatedChart-Square" component={AnimatedChart as unknown as React.ComponentType<Record<string, unknown>>}
        durationInFrames={240} fps={30} width={1080} height={1080}
        defaultProps={{
          title: "Week Record",
          overallWinRate: 77, overallRecord: "23-7",
          accentColor: "#38bdf8", brandName: "POWERPICKS",
          weeks: [
            { label: "Mon", wins: 2, losses: 1, winRate: 67 },
            { label: "Tue", wins: 3, losses: 0, winRate: 100 },
            { label: "Wed", wins: 1, losses: 1, winRate: 50 },
            { label: "Thu", wins: 2, losses: 0, winRate: 100 },
            { label: "Fri", wins: 2, losses: 1, winRate: 67 },
          ],
        }}
      />
    </>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
